import { Component, inject } from '@angular/core';
import { BaseListComponent } from '../base-list/base-list.component';
import { Commune } from '../../../models/commune';
import { MenuItem, MessageService, PrimeTemplate } from 'primeng/api';
import { SidebarService } from '../../../services/menu/sidebar.service';
import {
  ActivatedRoute,
  Router,
  RouterLink,
  RouterOutlet,
} from '@angular/router';
import { ConfirmationDialogService } from '../../../services/confirmation-dialog.service';
import { RoutingService } from '../../../services/routing.service';
import { Bookmark } from '../../../models/bookmark';
import { DividerModule } from 'primeng/divider';
import { HeaderComponent } from '../../atoms/header/header.component';
import { MaskComponent } from '../mask/mask.component';
import { DatePipe, NgIf } from '@angular/common';
import { SidebarModule } from 'primeng/sidebar';
import { TableCaptionComponent } from '../../molecules/table-list-caption/table-list-caption.component';
import { TableModule } from 'primeng/table';
import { BookmarkStore } from '../../../stores/bookmark/bookmark.state';
import { Button, ButtonDirective } from 'primeng/button';
import { GuestLinkService } from '../../../services/bookmark/guest-link.service';
import { BookmarkCategoryStore } from '../../../stores/bookmark-category/bookmark-category.state';
import { TagModule } from 'primeng/tag';
import { UserStore } from '../../../stores/user/user.state';
import { isExpired } from '../../../utility/app.utility';

@Component({
  selector: 'app-guest-link-list',
  standalone: true,
  imports: [
    DividerModule,
    HeaderComponent,
    MaskComponent,
    NgIf,
    PrimeTemplate,
    RouterOutlet,
    SidebarModule,
    TableCaptionComponent,
    TableModule,
    ButtonDirective,
    DatePipe,
    TagModule,
    RouterLink,
    Button,
  ],
  templateUrl: './guest-link-list.component.html',
  styleUrl: './guest-link-list.component.scss',
})
export class GuestLinkListComponent extends BaseListComponent<Bookmark> {
  readonly store = inject(BookmarkStore);
  readonly userStore = inject(UserStore);
  readonly categoryStore = inject(BookmarkCategoryStore);
  activeCommune: Commune | null = null;
  override menuItems: MenuItem[] = [];
  override sidebarId = 'bookmark-guest-link';
  now = new Date();

  constructor(
    protected sidebarService: SidebarService,
    router: Router,
    route: ActivatedRoute,
    messageService: MessageService,
    confirmationDialogService: ConfirmationDialogService,
    routingService: RoutingService,
    private guestLinkService: GuestLinkService
  ) {
    super(
      router,
      route,
      messageService,
      confirmationDialogService,
      routingService
    );
  }

  /**
   * Retrieves the category title for a given category ID.
   *
   * @param {string} id - The unique identifier of the category.
   * @return {string} The title of the category if found, otherwise returns 'nicht zugeordnet'.
   */
  getCategory(id: string): string {
    return this.categoryStore.getById(id)?.title || 'nicht zugeordnet';
  }

  /**
   * Copies the guest link token from a given bookmark to the clipboard,
   * if the token exists in the bookmark.
   *
   * @param {Bookmark} bookmark - The bookmark object containing the guest link token to be copied.
   * @return {void} This method does not return a value.
   */
  copy(bookmark: Bookmark) {
    const token = bookmark.guestLink?.token;
    if (token) this.guestLinkService.copyGuestLink(token);
  }

  /**
   * Opens a guest link associated with the provided bookmark, if available.
   *
   * @param {Bookmark} bookmark - The bookmark object containing the guest link information.
   * @return {void} No return value.
   */
  open(bookmark: Bookmark) {
    const token = bookmark.guestLink?.token;
    if (token) this.guestLinkService.openGuestLink(token);
  }

  /**
   * Removes the guest link associated with the given bookmark after user confirmation.
   * Updates the bookmark in the store after the guest link is removed.
   *
   * @param {Bookmark} bookmark - The bookmark object whose guest link is to be removed.
   * @return {void} This method does not return a value.
   */
  removeLink(bookmark: Bookmark) {
    this.store.singleSelection(bookmark._id);
    setTimeout(
      () =>
        this.confirmationDialogService.confirmBookmarkGuestLinkDeletion(
          undefined,
          bookmark,
          () => {
            bookmark.guestLink = undefined;
            this.store.update(bookmark);
          }
        ),
      300
    );
  }

  /**
   * Toggles the guest link status of a given bookmark.
   *
   * @param {Bookmark} bookmark - The bookmark object whose guest link status is to be toggled.
   * @param {boolean} status - The new status to set for the guest link (true to enable, false to disable).
   * @return {void} This method does not return a value.
   */
  toggleGuestLink(bookmark: Bookmark, status: boolean) {
    this.store.singleSelection(bookmark._id);
    setTimeout(
      () =>
        this.confirmationDialogService.confirmBookmarkGuestLinkToggle(
          undefined,
          bookmark,
          () => {
            if (!bookmark.guestLink) return;
            bookmark.guestLink.enabled = status;
            this.store.update(bookmark);
          }
        ),
      300
    );
  }

  /**
   * Copies the given password to the clipboard using the guestLinkService.
   *
   * @param {string} pw - The password to be copied to the clipboard.
   * @return {void} Does not return a value.
   */
  copyPassword(pw: string) {
    this.guestLinkService.copyPasswordToClipboard(pw);
  }

  protected readonly isExpired = isExpired;
}
